import {
  ArrowRight,
  Building2,
  Check,
  Clock3,
  FileCheck2,
  Paperclip,
  RotateCcw,
  Save,
  Trash2,
  Upload,
  UserRound,
} from "lucide-react";
import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { applicationStatus, formatDate, StatusBadge } from "../components/Badge";
import { Field, Input, Select, Textarea } from "../components/Form";
import { ErrorState, Failure, Loading, Success } from "../components/State";
import { api, idempotencyKey, json } from "../lib/api";
import type {
  AdmissionProgram,
  Application,
  JSONSchemaProperty,
  MaterialRequirement,
  Venture,
} from "../lib/types";
import { useAsync } from "../lib/useAsync";

const blank: Partial<Application> = {
  applicant_name: "",
  has_company: false,
  company_name: "",
  credit_code: "",
  registration_location: "",
  established_at: "",
  industry: "",
  business_description: "",
  ai_direction: "",
  team_size: "1",
  settlement_type: "desk",
  expected_support: [],
  other_needs: "",
  attachments: [],
  extra_data: {},
};

const editable = new Set(["draft", "supplement"]);
const terminal = new Set(["rejected", "withdrawn"]);

export function validateApplicationForm(
  values: Partial<Application>,
  submitting: boolean,
  program?: AdmissionProgram,
) {
  if (submitting && !values.applicant_name?.trim()) return "请填写申请人姓名";
  if (submitting && !values.business_description?.trim()) return "请填写主营业务简介";
  if (values.has_company && submitting && !values.company_name?.trim()) return "请填写公司名称";
  if (values.has_company && submitting && !/^[0-9A-HJ-NPQRTUWXY]{18}$/.test(values.credit_code ?? "")) return "请填写有效的统一社会信用代码";
  if (!submitting || !program) return "";

  const extra = values.extra_data ?? {};
  for (const key of program.schema.required ?? []) {
    const value = extra[key];
    if (value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0)) {
      return `请填写${program.schema.properties?.[key]?.title ?? key}`;
    }
  }
  for (const material of applicableMaterials(program.materials, values)) {
    const requiredCount = material.required_when ? Math.max(1, material.min_files) : material.min_files;
    const count = (values.attachments ?? []).filter((item) => item.kind === material.code).length;
    if (count < requiredCount) return `请上传${material.label}`;
  }
  return "";
}

export function materialConditionMatches(expression: string | null | undefined, values: Partial<Application>) {
  if (!expression?.trim()) return true;
  const match = expression.trim().match(/^([a-z][a-z0-9_]*)\s+in\s+\(([a-z0-9_,\s-]+)\)$/i);
  if (!match) return false;
  const context: Record<string, string> = {
    venture_kind: values.has_company ? "company" : "unregistered_project",
    settlement_mode: values.settlement_type ?? "desk",
  };
  const allowed = match[2].split(",").map((item) => item.trim());
  return allowed.includes(context[match[1]] ?? "");
}

function applicableMaterials(materials: MaterialRequirement[], values: Partial<Application>) {
  return materials.filter((material) => materialConditionMatches(material.required_when, values));
}

export function ApplicationPage() {
  const result = useAsync(async () => {
    const list = await api<{ items: Application[] }>("/applications");
    const current = list.items?.[0];
    return current ? api<Application>(`/applications/${current.id}`) : null;
  }, []);
  const programs = useAsync(() => api<{ items: AdmissionProgram[] }>("/admission/programs"), []);
  const ventures = useAsync(() => api<{ items: Venture[] }>("/ventures"), []);
  const [form, setForm] = useState<Partial<Application>>(blank);
  const [reapplying, setReapplying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingCode, setUploadingCode] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const application = reapplying ? null : result.data;
  const availablePrograms = useMemo(() => programs.data?.items ?? [], [programs.data?.items]);
  const selectedProgram = availablePrograms.find((item) => item.id === form.program_id) ?? availablePrograms[0];

  useEffect(() => {
    if (result.data && !reapplying) {
      setForm({ ...blank, ...result.data, extra_data: result.data.extra_data ?? {} });
      return;
    }
    if (!result.data || reapplying) {
      setForm((current) => ({
        ...blank,
        ...current,
        program_id: current.program_id || availablePrograms[0]?.id,
        venture_id: current.venture_id || ventures.data?.items[0]?.id,
      }));
    }
  }, [availablePrograms, reapplying, result.data, ventures.data?.items]);

  const materials = useMemo(
    () => applicableMaterials(selectedProgram?.materials ?? [], form),
    [form, selectedProgram],
  );

  if (result.loading || programs.loading || ventures.loading) return <Loading label="正在加载申请资料" />;
  if (result.error) return <ErrorState error={result.error} retry={result.retry} />;
  if (programs.error) return <ErrorState error={programs.error} retry={programs.retry} />;
  if (ventures.error) return <ErrorState error={ventures.error} retry={ventures.retry} />;
  if (!availablePrograms.length) return <ErrorState error={new Error("当前没有开放的入驻项目")} retry={programs.retry} />;

  const canEdit = !application || editable.has(application.status);
  const change = (key: keyof Application, value: unknown) => setForm((current) => ({ ...current, [key]: value }));
  const changeExtra = (key: string, value: unknown) => setForm((current) => ({
    ...current,
    extra_data: { ...(current.extra_data ?? {}), [key]: value },
  }));
  const selectVenture = (ventureID: string) => {
    const venture = ventures.data?.items.find((item) => item.id === ventureID);
    setForm((current) => venture ? {
      ...current,
      venture_id: venture.id,
      has_company: venture.kind === "company" || venture.kind === "sole_trader",
      company_name: venture.legal_name || venture.display_name,
      credit_code: venture.unified_social_credit_code || "",
      registration_location: venture.registration_region_code || "",
      established_at: venture.established_on || "",
    } : { ...current, venture_id: undefined });
  };

  const persist = async (values = form, isSubmitting = false) => {
    const program = availablePrograms.find((item) => item.id === values.program_id) ?? availablePrograms[0];
    const problem = validateApplicationForm(values, isSubmitting, program);
    if (problem) throw new Error(problem);
    const body = {
      applicant_name: values.applicant_name,
      company_registered: values.has_company,
      company_name: values.company_name,
      credit_code: values.credit_code,
      registration_place: values.registration_location,
      established_on: values.established_at || null,
      industry: values.industry,
      business_description: values.business_description,
      ai_direction: values.ai_direction,
      team_size: Number(values.team_size) || 1,
      settlement_mode: values.settlement_type,
      expected_support: values.expected_support || [],
      other_needs: values.other_needs,
      extra_data: values.extra_data ?? {},
      program_id: program.id,
      attachments: (values.attachments || []).map((file) => ({ file_id: file.id, kind: file.kind })),
      version: application?.version,
    };
    const summary = application
      ? await api<Application>(`/applications/${application.id}`, { method: "PATCH", body: json(body) })
      : await api<Application>("/applications", { method: "POST", body: json(body) });
    const saved = await api<Application>(`/applications/${summary.id}`);
    result.setData(saved);
    setReapplying(false);
    setForm({ ...blank, ...saved, extra_data: saved.extra_data ?? {} });
    return saved;
  };

  const save = async (event?: FormEvent) => {
    event?.preventDefault();
    setSaving(true);
    setMessage("");
    setError("");
    try {
      await persist();
      setMessage("申请草稿已保存");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "保存失败");
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    setMessage("");
    setError("");
    try {
      const saved = await persist(form, true);
      await api<Application>(`/applications/${saved.id}/submit`, {
        method: "POST",
        body: json({ version: saved.version, idempotency_key: idempotencyKey() }),
      });
      const submitted = await api<Application>(`/applications/${saved.id}`);
      result.setData(submitted);
      setForm({ ...blank, ...submitted, extra_data: submitted.extra_data ?? {} });
      setMessage("申请已正式提交");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "提交失败");
    } finally {
      setSubmitting(false);
    }
  };

  const upload = async (event: ChangeEvent<HTMLInputElement>, requirement: MaterialRequirement) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    if (!application?.id) {
      setError("请先保存草稿，再上传附件");
      event.target.value = "";
      return;
    }
    const existing = (form.attachments ?? []).filter((item) => item.kind === requirement.code).length;
    if (existing + files.length > requirement.max_files) {
      setError(`${requirement.label}最多上传 ${requirement.max_files} 个文件`);
      event.target.value = "";
      return;
    }
    setUploadingCode(requirement.code);
    setError("");
    try {
      const uploaded = [];
      for (const file of files) {
        if (file.size > requirement.max_size) throw new Error(`${file.name}超过材料大小限制`);
        if (requirement.content_types.length && !requirement.content_types.includes(file.type)) {
          throw new Error(`${file.name}的文件类型不符合材料要求`);
        }
        const body = new FormData();
        body.append("file", file);
        uploaded.push(await api<{ id: string; name: string; size: number }>("/uploads", { method: "POST", body }));
      }
      const next = {
        ...form,
        attachments: [
          ...(form.attachments || []),
          ...uploaded.map((file) => ({ ...file, kind: requirement.code })),
        ],
      };
      setForm(next);
      await persist(next);
      setMessage("材料已上传并关联到申请");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "附件上传失败");
    } finally {
      setUploadingCode("");
      event.target.value = "";
    }
  };

  const removeAttachment = async (fileID: string) => {
    const next = { ...form, attachments: (form.attachments ?? []).filter((item) => item.id !== fileID) };
    setForm(next);
    setSaving(true);
    setError("");
    try {
      await persist(next);
      setMessage("材料已移除");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "移除材料失败");
    } finally {
      setSaving(false);
    }
  };

  if (application && !canEdit) {
    return (
      <ApplicationStatusView
        application={application}
        onReapply={terminal.has(application.status) ? () => {
          setReapplying(true);
          setForm({ ...blank, program_id: availablePrograms[0].id });
          setMessage("");
          setError("");
        } : undefined}
        onChanged={(updated) => result.setData(updated)}
      />
    );
  }

  return (
    <div className="application-page">
      <aside className="application-aside">
        <span>SETTLEMENT APPLICATION</span>
        <h1>{application ? application.status === "supplement" ? "补充入驻资料" : "修改入驻申请" : "申请入驻园区"}</h1>
        <p>填写真实、完整的信息有助于园区更快了解项目。手机号自动取自登录账号，无需重复填写。</p>
        <ol><li className="active"><span>1</span>填写申请资料</li><li><span>2</span>园区审核</li><li><span>3</span>确认入驻</li><li><span>4</span>服务开通</li></ol>
        {application && <div className="application-id"><small>申请编号</small><strong>{application.application_no || application.id}</strong><StatusBadge status={application.status} /></div>}
      </aside>
      <main className="application-form-wrap">
        <form className="application-form" onSubmit={save}>
          <header><span>APPLICATION FORM</span><h2>入驻申请资料</h2><p>带 * 的项目为必填项。</p></header>
          {application?.reviewer_comment && <div className="review-notice"><strong>审核意见</strong><p>{application.reviewer_comment}</p></div>}
          {message && <Success>{message}</Success>}
          {error && <Failure>{error}</Failure>}

          <FormSection title="入驻项目" icon={<Building2 size={18} />}>
            <div className="form-grid cols-2"><Field label="创业主体" required hint="可在“我的 - 创业主体”中维护主体和成员"><Select value={form.venture_id || ""} disabled={Boolean(application)} onChange={(event) => selectVenture(event.target.value)}><option value="">根据本申请新建主体</option>{ventures.data?.items.map((item) => <option key={item.id} value={item.id}>{item.display_name}</option>)}</Select></Field><Field label="申请项目" required hint={selectedProgram?.description}><Select value={selectedProgram?.id} disabled={Boolean(application)} onChange={(event) => change("program_id", event.target.value)}>{availablePrograms.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</Select></Field></div>
          </FormSection>

          <FormSection title="申请人信息" icon={<UserRound size={18} />}>
            <div className="form-grid cols-2">
              <Field label="申请人姓名" required><Input required maxLength={50} value={form.applicant_name || ""} onChange={(event) => change("applicant_name", event.target.value)} /></Field>
              <Field label="登录手机号" hint="手机号由登录账号自动带入，不可修改"><Input disabled value={application?.phone || "已绑定当前登录账号"} /></Field>
            </div>
          </FormSection>

          <FormSection title="企业信息" icon={<Building2 size={18} />}>
            <label className="check-field"><input type="checkbox" checked={Boolean(form.has_company)} onChange={(event) => change("has_company", event.target.checked)} /><span><strong>已注册公司</strong><small>勾选后填写企业注册信息</small></span></label>
            {form.has_company && <div className="form-grid cols-2">
              <Field label="公司名称" required><Input required value={form.company_name || ""} onChange={(event) => change("company_name", event.target.value)} /></Field>
              <Field label="统一社会信用代码" required><Input required maxLength={18} value={form.credit_code || ""} onChange={(event) => change("credit_code", event.target.value.toUpperCase())} /></Field>
              <Field label="注册地区代码" hint="国家标准 6 位行政区划代码"><Input pattern="[0-9]{6}" maxLength={6} value={form.registration_location || ""} onChange={(event) => change("registration_location", event.target.value.replace(/\D/g, ""))} /></Field>
              <Field label="成立时间"><Input type="date" value={form.established_at?.slice(0, 10) || ""} onChange={(event) => change("established_at", event.target.value)} /></Field>
            </div>}
          </FormSection>

          <FormSection title="项目与团队" icon={<FileCheck2 size={18} />}>
            <div className="form-grid cols-2">
              <Field label="所属行业 / 赛道"><Input value={form.industry || ""} onChange={(event) => change("industry", event.target.value)} placeholder="如：人工智能、企业服务" /></Field>
              <Field label="团队规模"><Input type="number" min="1" max="999" value={form.team_size || "1"} onChange={(event) => change("team_size", event.target.value)} /></Field>
              <Field label="主营业务简介" required><Textarea required rows={5} maxLength={2000} value={form.business_description || ""} onChange={(event) => change("business_description", event.target.value)} placeholder="介绍产品、客户与当前进展" /></Field>
              <Field label="AI 应用方向"><Textarea rows={5} maxLength={1000} value={form.ai_direction || ""} onChange={(event) => change("ai_direction", event.target.value)} placeholder="没有可留空" /></Field>
            </div>
          </FormSection>

          <FormSection title="入驻需求" icon={<Building2 size={18} />}>
            <div className="form-grid cols-2">
              <Field label="期望入驻方式"><Select value={form.settlement_type || "desk"} onChange={(event) => change("settlement_type", event.target.value)}><option value="desk">共享工位</option><option value="registered_address">地址挂靠</option><option value="online">纯线上服务</option></Select></Field>
              <Field label="期望支持"><div className="option-grid">{["政策咨询", "场地支持", "融资对接", "市场资源", "人才服务", "创业辅导"].map((item) => <label key={item}><input type="checkbox" checked={form.expected_support?.includes(item) || false} onChange={(event) => change("expected_support", event.target.checked ? [...(form.expected_support || []), item] : form.expected_support?.filter((value) => value !== item))} />{item}</label>)}</div></Field>
              <Field label="其他诉求"><Textarea rows={4} maxLength={1000} value={form.other_needs || ""} onChange={(event) => change("other_needs", event.target.value)} /></Field>
            </div>
          </FormSection>

          {selectedProgram && Object.keys(selectedProgram.schema.properties ?? {}).length > 0 && (
            <DynamicFormSection program={selectedProgram} values={form.extra_data ?? {}} onChange={changeExtra} />
          )}

          <FormSection title="申请材料" icon={<Paperclip size={18} />}>
            <p className="section-help">材料类型、大小和数量由所选项目的发布配置决定。上传后需通过安全扫描才能正式提交。</p>
            <div className="material-requirements">
              {materials.map((material) => {
                const files = (form.attachments ?? []).filter((item) => item.kind === material.code);
                const requiredCount = material.required_when ? Math.max(1, material.min_files) : material.min_files;
                return <article className="material-requirement" key={material.id}>
                  <div className="material-heading"><div><strong>{material.label}{requiredCount > 0 && <i>*</i>}</strong><small>{files.length} / {material.max_files} 个，单个不超过 {formatBytes(material.max_size)}{material.sensitive ? "，敏感材料" : ""}</small></div><label className={application && files.length < material.max_files ? "upload-button" : "upload-button disabled"}><Upload size={17} />{uploadingCode === material.code ? "上传中..." : "上传"}<input type="file" multiple disabled={!application || uploadingCode !== "" || files.length >= material.max_files} accept={material.content_types.join(",")} onChange={(event) => void upload(event, material)} /></label></div>
                  <div className="attachment-list">{files.map((file) => <span key={file.id}><a href={file.download_url || file.url} target="_blank" rel="noreferrer"><Paperclip size={15} />{file.filename || file.name}</a><button type="button" title="移除材料" aria-label={`移除${file.filename || file.name}`} onClick={() => void removeAttachment(file.id)}><Trash2 size={15} /></button></span>)}</div>
                </article>;
              })}
            </div>
            {!application && <small>保存草稿后即可上传材料</small>}
          </FormSection>

          <footer className="form-actions">
            <button className="secondary-button" type="button" onClick={() => void save()} disabled={saving || submitting}><Save size={16} />{saving ? "保存中..." : "保存草稿"}</button>
            <button className="primary-button" type="button" disabled={saving || submitting} onClick={submit}>{submitting ? "提交中..." : "正式提交"}<ArrowRight size={16} /></button>
          </footer>
        </form>
      </main>
    </div>
  );
}

function DynamicFormSection({ program, values, onChange }: {
  program: AdmissionProgram;
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}) {
  const properties = program.schema.properties ?? {};
  const order = program.ui_schema.order?.filter((key) => properties[key]) ?? [];
  const keys = [...order, ...Object.keys(properties).filter((key) => !order.includes(key))];
  const required = new Set(program.schema.required ?? []);
  return <FormSection title="补充信息" icon={<FileCheck2 size={18} />}><div className="form-grid cols-2">{keys.map((key) => <DynamicField key={key} name={key} property={properties[key]} required={required.has(key)} value={values[key]} onChange={(value) => onChange(key, value)} />)}</div></FormSection>;
}

function DynamicField({ name, property, required, value, onChange }: {
  name: string;
  property: JSONSchemaProperty;
  required: boolean;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const label = property.title || name;
  const hint = property.description;
  if (property.type === "boolean") return <Field label={label} required={required} hint={hint}><label className="check-field compact"><input type="checkbox" checked={Boolean(value)} onChange={(event) => onChange(event.target.checked)} /><span>是</span></label></Field>;
  if (property.type === "array" && property.items?.enum) return <Field label={label} required={required} hint={hint}><div className="option-grid">{property.items.enum.map((option, index) => <label key={String(option)}><input type="checkbox" checked={Array.isArray(value) && value.includes(option)} onChange={(event) => { const current = Array.isArray(value) ? value : []; onChange(event.target.checked ? [...current, option] : current.filter((item) => item !== option)); }} />{property.items?.enumNames?.[index] ?? String(option)}</label>)}</div></Field>;
  if (property.enum) return <Field label={label} required={required} hint={hint}><Select required={required} value={String(value ?? "")} onChange={(event) => onChange(property.type === "number" || property.type === "integer" ? Number(event.target.value) : event.target.value)}><option value="">请选择</option>{property.enum.map((option, index) => <option value={String(option)} key={String(option)}>{property.enumNames?.[index] ?? String(option)}</option>)}</Select></Field>;
  if (property.type === "number" || property.type === "integer") return <Field label={label} required={required} hint={hint}><Input required={required} type="number" step={property.type === "integer" ? 1 : "any"} min={property.minimum} max={property.maximum} value={value === undefined ? "" : String(value)} onChange={(event) => onChange(event.target.value === "" ? undefined : Number(event.target.value))} /></Field>;
  if ((property.maxLength ?? 0) > 160) return <Field label={label} required={required} hint={hint}><Textarea required={required} rows={4} minLength={property.minLength} maxLength={property.maxLength} value={String(value ?? "")} onChange={(event) => onChange(event.target.value)} /></Field>;
  return <Field label={label} required={required} hint={hint}><Input required={required} type={property.format === "date" ? "date" : property.format === "email" ? "email" : "text"} minLength={property.minLength} maxLength={property.maxLength} value={String(value ?? "")} onChange={(event) => onChange(event.target.value)} /></Field>;
}

function FormSection({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return <section className="form-section"><h3>{icon}{title}</h3>{children}</section>;
}

function settlementLabel(value?: string) {
  return ({ desk: "共享工位", registered_address: "地址挂靠", online: "纯线上服务" } as Record<string, string>)[value || ""] || value || "-";
}

function formatBytes(bytes: number) {
  return bytes >= 1024 * 1024 ? `${Math.round(bytes / 1024 / 1024)} MB` : `${Math.round(bytes / 1024)} KB`;
}

function ApplicationStatusView({ application, onReapply, onChanged }: {
  application: Application;
  onReapply?: () => void;
  onChanged: (application: Application) => void;
}) {
  const status = applicationStatus[application.status] ?? applicationStatus.draft;
  const [withdrawing, setWithdrawing] = useState(false);
  const [error, setError] = useState("");
  const withdraw = async () => {
    setWithdrawing(true);
    setError("");
    try {
      await api(`/applications/${application.id}/withdraw`, { method: "POST" });
      onChanged(await api<Application>(`/applications/${application.id}`));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "撤回失败");
    } finally {
      setWithdrawing(false);
    }
  };
  return <div className="status-page"><section className="status-hero"><div><span>MY SETTLEMENT</span><h1>申请进度</h1><p>申请编号：{application.application_no || application.id}</p></div><div className="status-summary"><StatusBadge status={application.status} /><strong>{status.next}</strong><span>更新于 {formatDate(application.updated_at)}</span></div></section>{error && <Failure>{error}</Failure>}<div className="status-grid"><article className="white-panel"><header className="panel-heading"><span>PROCESS TIMELINE</span><h2>办理时间线</h2></header><div className="timeline">{application.history?.length ? application.history.map((item, index) => <div key={item.id || index}><span><Check size={15} /></span><div><strong>{applicationStatus[item.to_status]?.label || item.to_status}</strong><p>{item.comment || "状态已更新"}</p><small>{item.operator_name || "系统"} · {formatDate(item.created_at)}</small></div></div>) : <div><span><Clock3 size={15} /></span><div><strong>{status.label}</strong><p>{application.reviewer_comment || status.next}</p><small>{formatDate(application.updated_at || application.created_at)}</small></div></div>}</div></article><aside><article className="next-card"><span>下一步</span><h3>{status.next}</h3><p>{application.status === "pending" ? "无需重复提交，关键状态变化将通过站内通知告知。" : application.status === "approved" ? "请按园区通知完成线下手续。" : terminal.has(application.status) ? "可基于新的项目配置再次提交申请，历史版本会完整保留。" : "当前无需额外操作。"}</p>{application.status === "settled" && <Link className="primary-button" to="/community">进入创业社区<ArrowRight size={16} /></Link>}{application.status === "pending" && <button className="secondary-button" type="button" disabled={withdrawing} onClick={() => void withdraw()}>{withdrawing ? "撤回中..." : "撤回申请"}</button>}{onReapply && <button className="primary-button" type="button" onClick={onReapply}><RotateCcw size={16} />重新申请</button>}</article><article className="white-panel application-summary"><h3>申请摘要</h3><dl><div><dt>申请人</dt><dd>{application.applicant_name}</dd></div><div><dt>企业/项目</dt><dd>{application.company_name || "个人创业项目"}</dd></div><div><dt>业务方向</dt><dd>{application.industry || application.business_description}</dd></div><div><dt>入驻方式</dt><dd>{settlementLabel(application.settlement_type)}</dd></div></dl></article></aside></div></div>;
}
